import { useEffect } from 'react';

declare global {
  interface Window {
    _gaQueue?: Array<{ eventName: string; parameters: Record<string, unknown> }>;
    _gaQueueListener?: boolean;
  }
}

/**
 * Production-only Core Web Vitals monitoring
 * Sends metrics to Google Analytics 4 for performance tracking
 */
export const useProductionWebVitals = (): void => {
  useEffect(() => {
    // Only run in production
    if (import.meta.env.DEV) return;

    // Check if gtag is available, but don't block WebVitals setup
    const checkGtagAvailability = () => {
      if (typeof window === 'undefined' || !window.gtag) {
        return false;
      }
      return true;
    };

    checkGtagAvailability();

    // Note: web-vitals functions don't return cleanup functions in this version

    // Core Web Vitals polyfill loader
    const loadWebVitals = async () => {
      try {
        // Dynamic import of web-vitals library
        const { onCLS, onINP, onFCP, onLCP, onTTFB } = await import('web-vitals');

        // CLS - Cumulative Layout Shift
        const _clsCleanup = onCLS(metric => {
          const rating = getRating(metric.value, { good: 0.1, poor: 0.25 });
          sendToGA('web_vitals', {
            name: 'CLS',
            value: Math.round(metric.value * 1000), // Convert to milliseconds for consistency
            event_category: 'Web Vitals',
            event_label: rating,
            custom_map: { metric_value: metric.value },
          });
        });

        // INP - Interaction to Next Paint (replaces FID)
        const _inpCleanup = onINP(metric => {
          const rating = getRating(metric.value, { good: 200, poor: 500 });
          sendToGA('web_vitals', {
            name: 'INP',
            value: Math.round(metric.value),
            event_category: 'Web Vitals',
            event_label: rating,
          });
        });

        // FCP - First Contentful Paint
        const _fcpCleanup = onFCP(metric => {
          const rating = getRating(metric.value, { good: 1800, poor: 3000 });
          sendToGA('web_vitals', {
            name: 'FCP',
            value: Math.round(metric.value),
            event_category: 'Web Vitals',
            event_label: rating,
          });
        });

        // LCP - Largest Contentful Paint
        const _lcpCleanup = onLCP(metric => {
          const rating = getRating(metric.value, { good: 2500, poor: 4000 });
          sendToGA('web_vitals', {
            name: 'LCP',
            value: Math.round(metric.value),
            event_category: 'Web Vitals',
            event_label: rating,
          });
        });

        // TTFB - Time to First Byte
        const _ttfbCleanup = onTTFB(metric => {
          const rating = getRating(metric.value, { good: 800, poor: 1800 });
          sendToGA('web_vitals', {
            name: 'TTFB',
            value: Math.round(metric.value),
            event_category: 'Web Vitals',
            event_label: rating,
          });
        });

        // web-vitals automatically handles cleanup when the page unloads
      } catch {
        // Failed to load web-vitals library
      }
    };

    loadWebVitals().catch(() => {
      // Ignore web vitals loading errors
    });

    // No manual cleanup needed - web-vitals handles this automatically
  }, []);
};

/**
 * Get performance rating based on thresholds
 */
const getRating = (value: number, thresholds: { good: number; poor: number }) => {
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
};

/**
 * Send event to Google Analytics 4
 */
const sendToGA = (eventName: string, parameters: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...parameters,
      non_interaction: true, // Don't affect bounce rate
    });
  } else {
    // Queue the event to be sent when gtag becomes available
    if (typeof window !== 'undefined') {
      window._gaQueue = window._gaQueue ?? [];
      window._gaQueue.push({ eventName, parameters });

      // Set up a listener for when gtag becomes available
      if (!window._gaQueueListener) {
        window._gaQueueListener = true;
        const checkGtag = () => {
          if (window.gtag && window._gaQueue) {
            for (const { eventName, parameters } of window._gaQueue) {
              window.gtag('event', eventName, {
                ...parameters,
                non_interaction: true,
              });
            }
            window._gaQueue = [];
          } else {
            setTimeout(checkGtag, 1000);
          }
        };
        setTimeout(checkGtag, 1000);
      }
    }
  }
};

/**
 * Additional performance metrics for production monitoring
 */
export const useProductionPerformanceMetrics = (): void => {
  useEffect(() => {
    if (import.meta.env.DEV) return;

    // Track page load performance
    const trackPageLoad = () => {
      setTimeout(() => {
        const timing = window.performance.timing;
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;

        sendToGA('page_performance', {
          page_load_time: pageLoadTime,
          dom_ready_time: domReadyTime,
          event_category: 'Performance',
        });
      }, 0);
    };

    // Track when page becomes visible (user actually sees it)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        trackPageLoad();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    if (document.visibilityState === 'visible') {
      trackPageLoad();
    } else {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};

export default useProductionWebVitals;
